import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import mediaQueries from 'shared/helpers/styled-components/mediaQueries';
import TextAutoComplete from 'shared/components/presentational/TextAutoComplete/TextAutoComplete';
import BaseButton from 'shared/components/presentational/Button';
import { FormattedMapAddress } from '../helpers/formatAddresses';
import { fetchAddresses } from '../helpers';

const Wrapper = styled.div`
  width: 100%;
  display: flex;

  ${mediaQueries.toPhablet`
      flex-direction: column;
      padding: 0 10px;
      margin-bottom: 5px;
  `};
`;

const Button = styled(BaseButton)`
  margin-left: 5px;

  ${mediaQueries.fromTablet`
    margin-left: 10px;
  `};
`;

interface Props {
  className?: string;
  onSearch: (address: FormattedMapAddress) => void;
}

export default function MapSearch({ className, onSearch }: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<FormattedMapAddress[]>([]);

  const handleChange = useCallback((value: string) => {
    if (value) {
      fetchAddresses(value).then(addresses => {
        setItems(addresses);
      });
    } else {
      setItems([]);
    }
  }, []);

  const handleSearch = useCallback(() => {
    onSearch(items[0]);
  }, [onSearch, items]);

  const handleInputKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.code === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <Wrapper className={className}>
      <TextAutoComplete
        onChange={handleChange}
        items={items}
        placeholder={t('blocks.map.findAddress')}
        inputProps={{ onKeyPress: handleInputKeyPress }}
      />

      <Button
        variant="primary"
        onClick={handleSearch}
        disabled={items.length < 1}
      >
        <Button.children.Text>
          {t('blocks.map.findAction')}
        </Button.children.Text>
      </Button>
    </Wrapper>
  );
}
